<template>
  <button
    type="button"
    class="apple-pay-button"
    :class="{ submitting: submitting }"
    :disabled="disabled || submitting"
    :aria-label="ariaLabel"
    @click="handleClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  disabled?: boolean
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  submitting: false,
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const { t } = useI18n()

const ariaLabel = computed(() => {
  return props.submitting ? t('Processing…') : t('Book now')
})

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.submitting) {
    emit('click', event)
  }
}
</script>

<style scoped>
/**
 * Apple Pay Button Styles
 * Using official Apple Pay button CSS via -webkit-appearance
 * Source: https://developer.apple.com/documentation/applepayontheweb/displaying-apple-pay-buttons-using-css
 */

/**
 * Modern Apple Pay Button (Safari with -webkit-appearance support)
 * This property is built into Safari and automatically renders the official Apple Pay button
 */
@supports (-webkit-appearance: -apple-pay-button) {
  .apple-pay-button {
    display: block;
    width: 100%;
    min-height: 48px;
    height: 48px;
    -webkit-appearance: -apple-pay-button;
    -apple-pay-button-type: book; /* "Book with Apple Pay" */
    -apple-pay-button-style: black; /* Black button style */
    border-radius: 0.5em;
    cursor: pointer;
    border: none;
    padding: 0;
  }
}

/**
 * Fallback Apple Pay Button (older Safari versions)
 * For devices with earlier iOS or macOS versions where Safari doesn't support -webkit-appearance
 * Uses -webkit-named-image to display Apple Pay logo
 */
@supports not (-webkit-appearance: -apple-pay-button) {
  .apple-pay-button {
    display: block;
    width: 100%;
    min-height: 48px;
    height: 48px;
    background-color: black;
    background-image: -webkit-named-image(apple-pay-logo-white);
    background-size: 100% 60%;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    border-radius: 0.5em;
    padding: 0;
    border: none;
    box-sizing: border-box;
    cursor: pointer;
  }
}

/**
 * Hover state
 * Lighten background slightly on hover
 */
.apple-pay-button:hover:not(:disabled):not(.submitting) {
  filter: brightness(1.1);
}

/**
 * Active/pressed state
 * Darken background when button is pressed
 */
.apple-pay-button:active:not(:disabled):not(.submitting) {
  filter: brightness(0.9);
}

/**
 * Disabled state
 * Match BaseSubmitButton disabled styling
 */
.apple-pay-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/**
 * Submitting state
 * Reduce opacity to indicate processing
 */
.apple-pay-button.submitting {
  opacity: 0.7;
  cursor: wait;
  pointer-events: none;
}
</style>
